/**
 * Normalize Google Doc/Drive URLs stored with or without a protocol.
 */
export function normalizeGoogleUrl(value) {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

export function extractDocumentIdFromUrl(url) {
  const normalized = normalizeGoogleUrl(url);
  if (!normalized) return null;
  const match = normalized.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

export function extractFolderIdFromUrl(url) {
  const normalized = normalizeGoogleUrl(url);
  if (!normalized) return null;
  const match = normalized.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

export function isValidGoogleHttpUrl(value) {
  const normalized = normalizeGoogleUrl(value);
  if (!normalized) return false;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function formatGoogleContextError(error, { type = "doc", serviceAccountEmail = null } = {}) {
  const message = error?.message || String(error || "Unknown error");
  const status = error?.code || error?.status || error?.response?.status;

  if (status === 503 || /not available|not initialized|credentials/i.test(message)) {
    return "Google Docs integration is not configured on the server. An admin needs to set GOOGLE_SERVICE_ACCOUNT_KEY.";
  }

  if (
    status === 403 ||
    /403|permission|forbidden|insufficient|caller does not have/i.test(message)
  ) {
    return type === "drive"
      ? "Cannot access this Drive folder. The app’s Google service account does not have permission."
      : "Cannot access this Google Doc. The app’s Google service account does not have permission.";
  }

  if (status === 404 || /404|not found|file not found/i.test(message)) {
    return type === "drive"
      ? "This Drive folder was not found. Check the link, or whether the folder was moved or deleted."
      : "This Google Doc was not found. Check the link, or whether the doc was moved or deleted.";
  }

  if (/invalid|malformed|could not parse/i.test(message)) {
    return type === "drive"
      ? "This does not look like a valid Google Drive folder link. Use a URL like https://drive.google.com/drive/folders/FOLDER_ID."
      : "This does not look like a valid Google Doc link. Use a URL like https://docs.google.com/document/d/DOC_ID/edit.";
  }

  return type === "drive"
    ? `Could not load Drive folder: ${message}`
    : `Could not load Google Doc: ${message}`;
}

export function buildGoogleShareHint({
  type = "doc",
  serviceAccountEmail = null,
  shareWithEmail = null,
} = {}) {
  const email = shareWithEmail || serviceAccountEmail;
  const target = type === "drive" ? "folder" : "doc";
  if (email) {
    return `In Google, open Share and add ${email} with Viewer access (or higher).`;
  }
  return `Share this ${target} with the app’s Google service account (Viewer or higher). Ask an admin for the service account email.`;
}
